import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';

import AdminLayout from 'layouts/admin';
import CompanyLayout from 'layouts/empresa';
import AuthLayout from 'layouts/auth';
import AuthWrapper from 'components/auth/AuthWrapper';
import CookieBanner from 'components/cookies/CookieBanner';
import PaymentComplete from 'views/admin/main/payment/PaymentComplete';
import AddonPaymentComplete from 'views/admin/main/payment/AddonPaymentComplete';
import 'assets/css/Plugins.css';
import {
  TermsPage,
  PrivacyPage,
  CookiesPage,
  RefundPage,
  AUPPage,
} from 'views/legal';
import { BlogPage, BlogPostPage } from 'views/blog';
import CredentialViewPage from 'views/credentials/CredentialViewPage';
import { ROUTES } from 'constants/routes';
import config from 'config/environment';
import { NotificationProvider } from 'contexts/NotificationContext';

const isPreviewAuthBypassEnabled = () =>
  (process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_TRONA_PREVIEW_AUTH_BYPASS === 'true') ||
  config.auth0?.clientId === 'trona-preview-client';

const App = () => {
  // Lazy-initialize Stripe: only loads the 40KB SDK when App first renders,
  // and only if a public key is configured. useState with an initializer
  // guarantees a stable promise reference across re-renders.
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (isPreviewAuthBypassEnabled() || !config.stripe?.publicKey) return;

    let active = true;
    import('@stripe/stripe-js').then(({ loadStripe }) => {
      if (!active) return;
      setStripePromise(loadStripe(config.stripe.publicKey));
    });

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Routes>
        {/* Public routes - accessible without authentication (legal pages) */}
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="cookies" element={<CookiesPage />} />
        <Route path="refund-policy" element={<RefundPage />} />
        <Route path="acceptable-use" element={<AUPPage />} />
        {/* Blog routes - public, no auth required */}
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        {/* Credential view page - public, token-based auth */}
        <Route path="credentials" element={<CredentialViewPage />} />
        {/* Legacy routes for backwards compatibility */}
        <Route path="terminos" element={<Navigate to="/terms" replace />} />
        <Route path="privacidad" element={<Navigate to="/privacy" replace />} />

        {/* All other routes wrapped with AuthWrapper */}
        <Route
          path="*"
          element={
            <AuthWrapper>
              <NotificationProvider>
                <Routes>
                {/* Auth routes (login, signup) - shown when not authenticated */}
                <Route path="auth/*" element={<AuthLayout />} />

                {/* Payment completion page - handles redirect from Stripe crypto checkout */}
                <Route
                  path="payment-complete"
                  element={
                    stripePromise ? (
                      <Elements stripe={stripePromise}>
                        <PaymentComplete />
                      </Elements>
                    ) : (
                      <PaymentComplete />
                    )
                  }
                />

                {/* Addon payment completion page - handles redirect from Stripe crypto checkout for addon purchases */}
                <Route
                  path="addon-payment-complete"
                  element={
                    stripePromise ? (
                      <Elements stripe={stripePromise}>
                        <AddonPaymentComplete />
                      </Elements>
                    ) : (
                      <AddonPaymentComplete />
                    )
                  }
                />

                {/* Protected app routes - only shown when authenticated */}
                <Route path="empresa/*" element={<CompanyLayout />} />
                <Route path="admin/panel" element={<AdminLayout />} />

                {/* Default redirect:
                  - If not authenticated: AuthWrapper redirects to Auth0
                  - If authenticated: Goes to /empresa/home (redirects to selected company dashboard)
              */}
                <Route
                  path="/"
                  element={<Navigate to={ROUTES.HOME} replace />}
                />
                </Routes>
              </NotificationProvider>
            </AuthWrapper>
          }
        />
      </Routes>

      {/* Global Cookie Banner */}
      <CookieBanner />
    </>
  );
};

export default App;
